import { useState, useEffect, useCallback } from 'react';
import { db } from '../firebase';
import {
    collection,
    onSnapshot,
    addDoc,
    updateDoc,
    deleteDoc,
    doc,
    serverTimestamp
} from 'firebase/firestore';

export const useCustomTabs = (userId) => {
    const [tabs, setTabs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!userId) {
            setLoading(false);
            return;
        }

        const tabsCollection = collection(db, `users/${userId}/tabs`);
        const unsubscribe = onSnapshot(tabsCollection, (snapshot) => {
            const tabsList = snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
            setTabs(tabsList);
            setLoading(false);
        }, (err) => {
            console.error('Error loading custom tabs:', err);
            setError(err.message);
            setLoading(false);
        });

        return () => unsubscribe();
    }, [userId]);

    const addTab = useCallback(async (tabName) => {
        if (!userId) throw new Error("User ID is required to add a tab.");
        try {
            const tabsCollection = collection(db, `users/${userId}/tabs`);
            await addDoc(tabsCollection, {
                name: tabName,
                createdAt: serverTimestamp(),
                autoAddTags: [],
            });
        } catch (err) {
            console.error("Error adding tab:", err);
            throw err;
        }
    }, [userId]);

    const updateTab = useCallback(async (tabId, newName) => {
        if (!userId) throw new Error("User ID is required to update a tab.");
        try {
            const tabDoc = doc(db, `users/${userId}/tabs/${tabId}`);
            await updateDoc(tabDoc, {
                name: newName,
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error("Error updating tab:", err);
            throw err;
        }
    }, [userId]);

    const deleteTab = useCallback(async (tabId) => {
        if (!userId) throw new Error("User ID is required to delete a tab.");
        try {
            const tabDoc = doc(db, `users/${userId}/tabs/${tabId}`);
            await deleteDoc(tabDoc);
        } catch (err) {
            console.error("Error deleting tab:", err);
            throw err;
        }
    }, [userId]);

    const updateTabConfig = useCallback(async (tabId, config) => {
        if (!userId) throw new Error("User ID is required to update a tab.");
        try {
            const tabDoc = doc(db, `users/${userId}/tabs/${tabId}`);
            await updateDoc(tabDoc, {
                ...config,
                updatedAt: serverTimestamp()
            });
        } catch (err) {
            console.error("Error updating tab config:", err);
            throw err;
        }
    }, [userId]);

    return {
        tabs,
        loading,
        error,
        addTab,
        updateTab,
        deleteTab,
        updateTabConfig,
    };
};
